import { reactive, ref, Ref, toRef } from '@vue/composition-api';
import { TreeNodeModel } from 'tdesign-vue';

import { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';

interface TeamData {
  /** 左侧树形结构: 团队/成员结果 */
  teamTree: SMH.TeamTreeNode[];
}
interface QueryMembersParams {
  teamId: number;
  page?: number;
}

export default function handler(params?: { showPhoneNumber?: boolean }) {
  const treeKeys = {
    value: 'id',
    label: 'name',
    children: 'children',
  };

  const showPhoneNumber = params?.showPhoneNumber || false;

  let currTeamId: number | undefined;
  const rootTeamId = ref<number>();
  const teamData = reactive<TeamData>({
    teamTree: [],
  });

  const loading = reactive({
    team: false,
    moreUser: false,
  });

  const pageInfo = {
    pageSize: 1000,
    maxPage: 1, // 最多拉取maxPage页数据,存在性能问题，当前最多支持拉1000条数据
  };
  const loadInfo = reactive({
    userPage: 1,
    hasMoreUser: false,
    loadLimit: false,
  });

  const methods = {
    initData() {
      methods.initTeamTree();
    },
    async getTeamTree(teamId?: number | string) {
      let team: SMH.TeamTreeNode = {
        id: -1,
        name: '',
        userCount: 0,
        spaceId: '',
        parentId: -1,
        children: [],
      };
      loading.team = true;
      try {
        const { data } = await smh.userAPI.queryTeam({
          teamId: teamId || '',
          checkPermission: 0,
          // 默认展示无空间的团队
          withNoSpaceTeams: 1,
          // 默认只展示已激活的成员
          withActiveUserCount: 1,
        });
        team = {
          ...data,
          added: false,
          uid: `team_${data.id}`,
        };
        if (team.children) {
          team.children = team.children.map(child => ({
            ...child,
            added: false,
            uid: `team_${child.id}`,
          }));
        }
      } catch (error) {
        tips.error(i18n_t('拉取团队失败'));
      }
      loading.team = false;
      return team;
    },
    _queryRequest({ teamId, page = 1 }: QueryMembersParams) {
      return smh.userAPI.queryTeamMember({
        teamId,
        page,
        pageSize: pageInfo.pageSize,
        keyword: '',
        showPhoneNumber,
        includeInactiveUser: 0,
      });
    },
    // 每次拉取一页
    async queryUserList({ teamId, page = 1 }: QueryMembersParams) {
      const contents: SMH.TeamMemberItemData[] = [];
      try {
        const { data } = await methods._queryRequest({ teamId, page });
        contents.push(...data.contents);
        loadInfo.hasMoreUser = data.totalNum > pageInfo.pageSize * loadInfo.userPage;
        // 达到加载的最大限制
        loadInfo.loadLimit = loadInfo.hasMoreUser && loadInfo.userPage === pageInfo.maxPage;
      } catch (error) {
        tips.error(i18n_t('拉取成员失败'));
      }
      return contents;
    },
    // 一次拉取所有数据
    queryAllMembers({ teamId }: QueryMembersParams) {
      const totleTeamMember: SMH.TeamMemberItemData[] = [];
      const next = async (page: number): Promise<any> => {
        loading.team = true;
        try {
          const { data } = await methods._queryRequest({ teamId, page });
          totleTeamMember.push(...data.contents);
          if (data.totalNum > pageInfo.pageSize * page) {
            loading.team = false;
            return next(page + 1);
          }
        } catch (error) {
          tips.error(i18n_t('拉取成员失败'));
        }
        return totleTeamMember;
      };
      return next(1);
    },
    /**
     * 拉取用户列表
     * 1、根团队只拉一次更多数据需要手动拉取
     * 2、其他团队可以一次性拉取完
     * */
    async getTeamMemberTree(params: { isRoot?: boolean; teamId: number | string }): Promise<SMH.TeamMemberItemData[]> {
      const { isRoot = false, teamId } = params;
      const totleTeamMember: SMH.TeamMemberItemData[] = [];
      loading.team = true;
      let contents: SMH.TeamMemberItemData[] = [];
      if (isRoot) {
        // 加载根目录时重置更多加载的页数为1
        loadInfo.userPage = 1;
        contents = await methods.queryUserList({ teamId: +teamId });
      } else {
        contents = await methods.queryAllMembers({ teamId: +teamId });
      }
      totleTeamMember.push(...contents);
      loading.team = false;
      return totleTeamMember;
    },
    // 手动加载更多用户
    async loadMoreUser() {
      if (loading.moreUser) return;
      // 加载更多默认是第二页开始
      loadInfo.userPage++;
      loading.moreUser = true;
      const rootId = rootTeamId.value as number;
      const contents = await methods.queryUserList({ teamId: rootId, page: loadInfo.userPage });
      loading.moreUser = false;
      if (contents.length === 0) return;
      const formattedUserList = methods.formatUserList(contents, rootId);
      const treeData = teamData.teamTree[0];
      treeData.children?.push(...formattedUserList);
      teamData.teamTree = [treeData];
    },
    formatUserList(userList: SMH.TeamMemberItemData[], rootId: number) {
      return (userList as any).map((m: any) => {
        const { id } = m;
        m.id = `${rootId}_${m.id}`;
        if (currTeamId && m.canManageTeams.includes(currTeamId)) {
          m.role = 'teamAdmin';
        }
        return {
          ...m,
          name: m.nickname,
          userId: id,
          added: false,
          uid: `user_${id}`,
        };
      });
    },
    async initTeamTree(teamId?: number) {
      currTeamId = teamId;
      const root = await methods.getTeamTree();
      const rootMember = await methods.getTeamMemberTree({ isRoot: true, teamId: root.id });
      if (!root.children) {
        root.children = [];
      }
      root.children.forEach((child: any) => {
        child.children = (child.children && child.children.length > 0) || child.userCount > 0;
      });
      const formattedUserList = methods.formatUserList(rootMember, root.id);
      root.children.push(...formattedUserList);
      teamData.teamTree = [root];
      rootTeamId.value = root.id;
    },
    async loadTeamNode(node: TreeNodeModel) {
      const teamId = node.data?.id || '';
      const team = await methods.getTeamTree(teamId);
      const teamMember = await methods.getTeamMemberTree({ isRoot: false, teamId });
      if (team.children) {
        team.children.push(
          ...(teamMember as any).map((m: any) => {
            const { id } = m;
            m.id = `${team.id}_${m.id}`;
            if (currTeamId && m.canManageTeams.includes(currTeamId)) {
              m.role = 'teamAdmin';
            }
            return {
              ...m,
              name: m.nickname,
              userId: id,
              added: false,
              uid: `user_${id}`,
            };
          }),
        );
        const subTeam: any = team.children.map(item => ({
          ...item,
          children: (item.children && item.children.length > 0) || (item.userCount as number) > 0,
        }));
        methods.signTeamNode(teamId, subTeam);
        return subTeam;
      }
      return [];
    },
    signTeamNode(teamId: any, subTeam: any) {
      // 将新的数据加添加到对应的团队下面，形成完整的树形结构
      // 避免问题： 1. 使用搜索框搜索后，数据不对
      // 2. 在查看 word 等文件中，右上角添加共享，无法正确的查找到目标的添加状态
      const targetTeam = teamData.teamTree[0].children?.find(item => item.id === teamId);
      if (targetTeam) {
        targetTeam.children = [];
        targetTeam.children.push(...subTeam);
      }
    },
    resetData() {
      Object.assign(teamData, {
        teamTree: [],
      });
    },
  };

  return {
    /** 树结构渲染key */
    treeKeys,
    /** 根团队id */
    rootTeamId,
    teamData,
    loading,
    hasMoreUser: toRef(loadInfo, 'hasMoreUser'),
    loadLimit: toRef(loadInfo, 'loadLimit'),
    methods,
  };
}
